import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5555';

export const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/fleets`,
  headers: {
    'Content-Type': 'application/json',
  },
  // Live backends (e.g. Render) can cold-start >10s; retries then succeed once warm.
  timeout: 60000,
});

/** User-facing message for any axios error (avoids raw "Request failed with status code 401"). */
function normalizeAxiosErrorMessage(error) {
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. If the server was sleeping, try again in a few seconds.';
  }
  if (!error.response) {
    if (error.message === 'Network Error') {
      return 'Cannot connect to the server. Check your connection or try again shortly.';
    }
    return error.message || 'Network error';
  }
  const { status, data } = error.response;
  if (data && typeof data === 'object') {
    const m = data.message || data.error;
    if (m) return m;
    if (Array.isArray(data.errors) && data.errors[0]?.msg) return data.errors[0].msg;
  }
  if (typeof data === 'string' && data.trim()) {
    const stripped = data.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (stripped.length > 0 && stripped.length < 400) return stripped;
  }
  if (status === 401) {
    return 'Invalid credentials or you do not have access to the fleet portal.';
  }
  if (status === 403) return 'Access denied.';
  if (status === 404) return 'Service not found.';
  if (status === 429) return 'Too many requests. Please wait and try again.';
  if (status >= 500) return 'Server error. Please try again shortly.';
  return error.message || 'Request failed';
}

function isPublicAuthRequest(config) {
  const url = config.url || '';
  return url.includes('/auth/login') || url.includes('/auth/register');
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    if (!isPublicAuthRequest(config)) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        error.message = 'Request timeout. Please check your connection and try again.';
      } else if (error.message === 'Network Error') {
        error.message = 'Cannot connect to server. Please ensure the backend is running.';
      }
    }

    error.userMessage = normalizeAxiosErrorMessage(error);

    // Handle 401 unauthorized
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
